import React from 'react';
import {useTranslation} from 'react-i18next';

import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import {alpha} from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import {mislecAPI} from "../../util/axios";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
};

function DocumentModal({ user, updateUser, corpus, updateDocuments, documentModal, setDocumentModal, setOpen, setMessage, setSeverity }) {
    const [documents, setDocuments] = React.useState([]);
    const [selectedDocuments, setSelectedDocuments] = React.useState([]);
    const [fecthed, setFetched] = React.useState(false);

    const { t } = useTranslation();

    const fetchDocuments = async () => {
        //  Fetch documents from the server
        mislecAPI.get(`/api/documents/${user._id}`)
            .then(res => {
                setDocuments(res.data.documents);
                setFetched(true);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    React.useEffect(() => {
        if (!documentModal) {
            setSelectedDocuments([]);
        }
    }, [documentModal]);

    if (!fecthed) {
        fetchDocuments();
    }

    return (
        <Modal
            open={documentModal}
            onClose={() => setDocumentModal(false)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                sx={{
                    padding: '24px',
                    minWidth: { xs: '95%', md: '40%' },
                    maxWidth: { xs: '95%', md: '40%' },
                }}
            >
                <Typography variant='h6' sx={{ mb: 4 }}>
                    {t('corpus.modals.documents.title')}
                </Typography>
                <Stack spacing={4}>
                    <FormControl>
                        <InputLabel id='mISLec_corpus_documents_label'>{t('corpus.modals.documents.inputs.documents.label')}</InputLabel>
                        <Select
                            id='mISLec_corpus_documents'
                            labelId='mISLec_corpus_documents_label'
                            label={t('corpus.modals.documents.inputs.documents.label')}
                            multiple
                            value={selectedDocuments}
                            onChange={(e) => setSelectedDocuments(e.target.value)}
                            renderValue={(selectedDocuments) => selectedDocuments.map((document) => document.filename).join(', ')}
                            MenuProps={MenuProps}
                        >
                            {documents.length === 0 ?
                                <MenuItem disabled>
                                    <Typography variant='body2'>{t('corpus.modals.documents.error.noDocuments')}</Typography>
                                </MenuItem>
                                :
                                documents.map((document) => (
                                    <MenuItem
                                        key={document._id}
                                        value={document}
                                        disabled={corpus?.documents?.includes(document._id)}
                                    >
                                        <Typography variant='body2'>{document.filename}</Typography>
                                    </MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                </Stack>
                <Stack
                    direction='row'
                    alignItems='center'
                    mt={4}
                    sx={{
                        justifyContent: {
                            xs: 'space-between',
                            md: 'flex-end'
                        }
                    }}
                >
                    <Button
                        variant=''
                        onClick={() => setDocumentModal(false)}
                        sx={{

                            borderRadius: '8px !important',
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.error.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'error.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'error.main',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                            }
                        }}
                    >
                        {t('corpus.modals.documents.buttons.cancel')}
                    </Button>
                    <Button
                        variant=''
                        onClick={() => updateDocuments(selectedDocuments.map(document => document._id)) && setDocumentModal(false) && setSelectedDocuments([])}
                        sx={{
                            ml: {
                                xs: 0,
                                md: 1
                            },
                            borderRadius: '8px !important',
                            backgroundColor: (theme) => ({
                                xs: alpha(theme.palette.primary.main, 0.08),
                                md: 'rgba(33, 43, 54, 0.08)'
                            }),
                            color: {
                                xs: 'primary.main',
                                md: 'inherit'
                            },
                            '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                            }
                        }}
                    >
                        {t('corpus.modals.documents.buttons.add')}
                    </Button>
                </Stack>
            </Paper>
        </Modal>
    );
}

export default DocumentModal;